import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAuthContext } from 'context/authContext';
import {
  getJuzProgress,
  createJuzProgress,
  deleteJuzProgress,
  getCurrentJuzByOrphan
} from 'lib/supabaseQueries';

export function useJuzProgress(orphanId) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['juzProgress', orphanId],
    queryFn: () => getJuzProgress(orphanId),
    enabled: !!orphanId
  });
  const history = data?.data || [];
  return { history, current: history[0] || null, isLoading, refetch };
}

// Latest Juz per child, keyed by orphan id - for the beneficiaries list
export function useCurrentJuzByOrphan() {
  const { organization, isInitialized } = useAuthContext();
  const { data } = useQuery({
    queryKey: ['currentJuzByOrphan', organization?.id],
    queryFn: () => getCurrentJuzByOrphan(organization.id),
    enabled: isInitialized && !!organization?.id
  });
  return { currentJuzByOrphan: data?.data || {} };
}

export function useRecordJuzProgress() {
  const queryClient = useQueryClient();
  const { organization, user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async ({ orphanId, branchId, juz, recordedOn, notes }) => {
      const result = await createJuzProgress({
        orphan_id: orphanId,
        organisation_id: organization.id,
        branch_id: branchId || null,
        juz,
        recorded_on: recordedOn,
        notes: notes || null,
        recorded_by: user?.id || null
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['juzProgress'] });
      queryClient.invalidateQueries({ queryKey: ['currentJuzByOrphan'] });
      toast.success('Juz progress recorded');
    },
    onError: error => {
      console.error('Record Juz progress error:', error);
      toast.error(error.message || 'Failed to record progress');
    }
  });

  return {
    recordProgress: mutation.mutate,
    recordProgressAsync: mutation.mutateAsync,
    isRecording: mutation.isPending
  };
}

export function useDeleteJuzProgress() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteJuzProgress(id);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['juzProgress'] });
      queryClient.invalidateQueries({ queryKey: ['currentJuzByOrphan'] });
      toast.success('Entry removed');
    },
    onError: () => toast.error('Failed to remove entry')
  });
  return { deleteEntry: mutation.mutate, isDeleting: mutation.isPending };
}
